import { Fragment, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { SearchHit } from '@avslack/shared';
import { apiFetch, searchCanvases } from '../lib/api';
import { useUsers } from '../data/users';
import { Avatar } from './Avatar';
import { Icon } from './Icon';
import { useBackToClose } from '../lib/back-to-close';

/**
 * Kereses A BESZELGETESEN BELUL.
 *
 * Eddig csak a kozos kereso-oldal letezett: onnan a talalat egy masik nezetbe
 * vitt, es a beszelgetes elveszett a szem elol. A leggyakoribb kerdes viszont
 * az, hogy "hol is volt ez EBBEN a csatornaban" - arra ez a panel valaszol,
 * anelkul hogy elhagynad a beszelgetest.
 *
 * ─── Miert azonosito szerint szukitunk? ─────────────────────────────────────
 *
 * Az `in:#nev` szuro nevre illeszt, a DM-nek viszont NINCS neve. A
 * `channelId` minden fajtanal mukodik, es pontosan egy beszelgetest jelol ki -
 * nem egy hasonlo nevut is.
 */

/** Ennyit varunk gepeles utan. Eleg ahhoz, hogy ne kerdezgessunk betunkent. */
const DEBOUNCE_MS = 300;

interface SearchResponse {
  hits: SearchHit[];
  hasMore: boolean;
}

/**
 * Emlites-szuro.
 *
 * Ket KULONBOZO kerdes van, ezert ket kulon allapot - nem egy kapcsolo:
 *
 *  - "engem emlitenek": mi tartozik RAM. Ebbe a `@channel`, a `@here` es a
 *    sajat csoportjaim emlitese is beleszamit, mert azok is nekem szolnak.
 *  - "barkit emlitenek": hol OSZTOTTAK KI valamit, akarkinek. Ez akkor kell,
 *    amikor nem magamat keresem, hanem azt latnam, kire mi maradt.
 */
type MentionFilter = 'me' | 'any';

const MENTION_CHOICES: { value: MentionFilter | null; label: string; title: string }[] = [
  { value: null, label: 'Mind', title: 'Minden üzenet' },
  {
    value: 'me',
    label: 'Engem említ',
    title: 'Csak amiben engem említenek (@channel és @here is)',
  },
  { value: 'any', label: 'Bármely említés', title: 'Csak amiben bárkit említenek' },
];

export function ChannelSearchPanel({
  channelId,
  onJump,
  onOpenCanvas,
  onClose,
}: {
  channelId: string;
  /** Ugras a talalatra: a hivo gorget es kiemeli az uzenetet. */
  onJump: (messageId: string) => void;
  /** Vaszon-talalat: a hivo megnyitja a vaszonpanelt ezzel a vaszonnal. */
  onOpenCanvas: (canvasId: string) => void;
  onClose: () => void;
}) {
  // Telefonon a vissza-mozdulat ezt a panelt zarja (K2).
  useBackToClose(true, onClose);
  const [text, setText] = useState('');
  const [term, setTerm] = useState('');
  const [mentions, setMentions] = useState<MentionFilter | null>(null);
  /**
   * Csak a FONTOSNAK jeloltek.
   *
   * Kulon kapcsolo, nem az emlites-valaszto negyedik gombja: mas kerdes, es a
   * kettot egyutt is fel lehet tenni ("fontos, amiben engem emlitenek").
   */
  const [important, setImportant] = useState(false);
  const { nameOf } = useUsers();

  // A gepeles nem kerdez azonnal - kulonben minden betu egy lekerdezes lenne.
  useEffect(() => {
    const timer = setTimeout(() => setTerm(text.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [text]);

  /*
    A szuro MODOSITOKENT megy at, nem kulon mezokent.

    A szerver ugyanazt az ertelmezot hasznalja, ami a beirt `mentions:me`-t is
    felismeri - igy egyetlen ut van, amit tesztelni kell, es a gomb pontosan
    azt csinalja, amit a begepelt valtozat.
  */
  const q = useMemo(
    () =>
      [term, mentions ? `mentions:${mentions}` : '', important ? 'is:important' : '']
        .filter(Boolean)
        .join(' '),
    [term, mentions, important],
  );

  /*
    Az emlites-szuro ONMAGABAN is ervenyes kereses.

    Ket betu nelkul eddig nem kerdeztunk semmit - de a "mutasd, hol emlitettek
    engem" kerdeshez nincs mit begepelni. Ha van szuro, a szoveg elhagyhato.
  */
  const kereshet = mentions !== null || important || term.length >= 2;

  const query = useQuery({
    queryKey: ['channel-search', channelId, q],
    enabled: kereshet,
    queryFn: () =>
      apiFetch<SearchResponse>(
        `/api/search?q=${encodeURIComponent(q)}&channelId=${encodeURIComponent(channelId)}&sort=newest&limit=50`,
      ),
    staleTime: 30_000,
  });

  const hits = useMemo(() => query.data?.hits ?? [], [query.data]);

  /*
    A CSATORNA VASZNAI is ide tartoznak: a vaszon a csatornahoz kotodik, es
    aki itt keres, nem tudja (nem is kell tudnia), uzenetben vagy vasznon
    irtak le valamit. Az emlites- es fontos-szuro uzenetre ertelmes, azokkal
    vasznat nem keresunk.
  */
  const canvasQuery = useQuery({
    queryKey: ['channel-canvas-search', channelId, term],
    enabled: term.length >= 2 && mentions === null && !important,
    queryFn: () => searchCanvases(term, 10, channelId),
    staleTime: 30_000,
  });
  const canvasHits =
    term.length >= 2 && mentions === null && !important ? (canvasQuery.data ?? []) : [];

  /** A nulla talalat uzenetehez: mire szukitettunk a szovegen kivul. */
  const szukites = [
    mentions ? ' az említések között' : '',
    important ? ' a fontosak között' : '',
  ].join('');

  return (
    <aside className="flex w-80 shrink-0 flex-col border-l border-border-soft bg-surface">
      <header className="flex items-center gap-2 border-b border-border-soft px-3 py-2.5 transition-colors duration-(--duration-quick) focus-within:border-accent">
        <span className="text-ink-faint">
          <Icon name="keres" size="sm" />
        </span>
        <input
          aria-label="Keresés ebben a beszélgetésben"
          autoFocus
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => event.key === 'Escape' && onClose()}
          placeholder="Keresés ebben a beszélgetésben…"
          className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-ink-faint"
        />
        <button
          type="button"
          onClick={onClose}
          title="Bezárás"
          className="rounded-control p-1 text-ink-muted hover:text-ink"
        >
          <Icon name="bezaras" size="sm" />
        </button>
      </header>

      <div className="flex flex-wrap items-center gap-1 border-b border-border-soft px-3 py-2">
        {/*
          FONTOS-SZURO.

          Elol all, mert onmagaban is ertelmes kereses: a "mi az, amit nem
          szabad atgorgetni ebben a csatornaban" kerdesehez nincs mit
          begepelni.
        */}
        <button
          type="button"
          title="Csak a fontosnak jelölt üzenetek"
          aria-pressed={important}
          onClick={() => setImportant((current) => !current)}
          className={[
            'flex items-center gap-1 rounded-pill px-2.5 py-1 text-2xs font-medium',
            'transition-colors duration-(--duration-quick)',
            important
              ? 'bg-accent text-accent-ink'
              : 'bg-surface-sunken text-ink-muted hover:bg-surface-hover hover:text-ink',
          ].join(' ')}
        >
          <span aria-hidden="true">
            <Icon name="figyelmeztetes" size="sm" />
          </span>
          Fontos
        </button>

        <span aria-hidden="true" className="mx-0.5 h-4 w-px bg-border-soft" />

        {MENTION_CHOICES.map((choice) => {
          const aktiv = mentions === choice.value;
          return (
            <button
              key={choice.label}
              type="button"
              title={choice.title}
              aria-pressed={aktiv}
              onClick={() => setMentions(choice.value)}
              className={[
                'rounded-pill px-2.5 py-1 text-2xs font-medium transition-colors duration-(--duration-quick)',
                aktiv
                  ? 'bg-accent text-accent-ink'
                  : 'bg-surface-sunken text-ink-muted hover:bg-surface-hover hover:text-ink',
              ].join(' ')}
            >
              {choice.label}
            </button>
          );
        })}
      </div>

      {/*
        A felolvaso csak az EREDMENY SZAMAT kapja, mindig jelenlevo elemben -
        a teljes talalatlista elo regiokent minden gepelesnel vegigolvasodna.
      */}
      <p role="status" className="sr-only">
        {!kereshet
          ? ''
          : query.isLoading
            ? 'Keresés…'
            : query.isError
              ? ''
              : hits.length + canvasHits.length === 0
                ? 'Nincs találat.'
                : `${hits.length + canvasHits.length} találat.`}
      </p>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {!kereshet ? (
          <p className="px-3 py-4 text-xs text-ink-faint">
            Írj be legalább két betűt, vagy válassz egy szűrőt. A találatra kattintva a beszélgetés
            odagördül.
          </p>
        ) : query.isLoading ? (
          <p className="px-3 py-4 text-xs text-ink-faint">Keresés…</p>
        ) : query.isError ? (
          /*
            A HIBA NEM UGYANAZ, mint a nulla talalat.

            Korabban a ketto egyforman "Nincs találat"-kent latszott: ha a
            szerver elszallt, a felhasznalo azt hitte, tenyleg nincs ilyen
            uzenet - es tovabb kereste maskepp. A hiba szovege itt lathato,
            hogy legyen mit tovabbadni.
          */
          <div role="alert" className="px-3 py-4 text-xs">
            <p className="font-semibold text-danger">A keresés nem sikerült.</p>
            <p className="mt-1 text-ink-muted">
              {query.error instanceof Error ? query.error.message : 'Ismeretlen hiba.'}
            </p>
          </div>
        ) : hits.length === 0 && canvasHits.length === 0 ? (
          <p className="px-3 py-4 text-xs text-ink-faint">
            {term
              ? `Nincs találat erre: „${term}”${szukites}.`
              : mentions === 'me'
                ? 'Ebben a beszélgetésben nem említettek téged.'
                : mentions === 'any'
                  ? 'Ebben a beszélgetésben nincs említés.'
                  : 'Ebben a beszélgetésben nincs fontosnak jelölt üzenet.'}
          </p>
        ) : (
          <>
            {canvasHits.length > 0 && (
              <>
                <p className="flex items-center gap-1.5 px-3 py-2 text-2xs uppercase tracking-wide text-ink-faint">
                  <Icon name="canvas" size="sm" /> Vásznakban
                </p>
                <ul>
                  {canvasHits.map((hit) => (
                    <li key={hit.canvasId}>
                      <button
                        type="button"
                        onClick={() => onOpenCanvas(hit.canvasId)}
                        className="block w-full border-b border-border-soft px-3 py-2.5 text-left transition-colors hover:bg-surface-hover"
                      >
                        <span className="block truncate text-xs font-semibold text-accent">
                          <Highlighted text={hit.title} term={term} />
                        </span>
                        {hit.excerpt && (
                          <span className="mt-1 block break-words text-xs leading-relaxed text-ink-muted">
                            <Highlighted text={hit.excerpt} term={term} />
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {hits.length > 0 && (
              <p className="px-3 py-2 text-2xs uppercase tracking-wide text-ink-faint">
                {hits.length} találat az üzenetekben
                {query.data?.hasMore ? ' (a legutóbbiak)' : ''}
              </p>
            )}
            <ul>
              {hits.map((hit) => (
                <li key={hit.message.id}>
                  <button
                    type="button"
                    onClick={() => onJump(hit.message.id)}
                    className="block w-full border-b border-border-soft px-3 py-2.5 text-left transition-colors hover:bg-surface-hover"
                  >
                    <span className="flex items-center gap-2">
                      <Avatar
                        userId={hit.message.userId ?? ''}
                        name={nameOf(hit.message.userId)}
                        size="sm"
                      />
                      <span className="truncate text-xs font-semibold">
                        {nameOf(hit.message.userId)}
                      </span>
                      <span className="ml-auto shrink-0 text-2xs text-ink-faint">
                        {new Date(hit.message.createdAt).toLocaleString('hu-HU', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </span>
                    {/*
                      A SZOVEGKORNYEZET a szerverbol jon, `<<...>>` jelolokkel.
                      Szovegkent rakjuk ki, sosem HTML-kent.
                    */}
                    <span className="mt-1 block break-words text-xs leading-relaxed text-ink-muted">
                      <Highlighted text={hit.excerpt} term={term} />
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </aside>
  );
}

/**
 * Ekezet nelkuli valtozat, INDEXTERKEPPEL.
 *
 * A kiemeleshez tudni kell, hogy az ekezet nelkuli talalat hol kezdodik az
 * EREDETI szovegben - kulonben az "ölelés" szo kiemelese elcsuszna. Ezert
 * karakterenkent hajtogatunk, es megjegyezzuk, melyik hajtogatott betu
 * melyik eredeti betubol jott.
 */
function foldWithMap(text: string): { folded: string; map: number[] } {
  let folded = '';
  const map: number[] = [];

  for (let i = 0; i < text.length; i += 1) {
    const piece = text[i]!.normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase();
    for (const ch of piece) {
      folded += ch;
      map.push(i);
    }
  }
  map.push(text.length);

  return { folded, map };
}

/**
 * A szerver `<<...>>` jelolokkel adja meg, mit talalt.
 *
 * A szoveget darabokra bontjuk es SZOVEGKENT rakjuk ki - HTML soha. Egy
 * uzenet tartalma a felhasznalotol jon; HTML-kent beilleszteni akkor is
 * hiba lenne, ha ma epp artalmatlan.
 *
 * ─── Miert van sajat kiemeles is? ───────────────────────────────────────────
 *
 * A beszelgetesen beluli kereso a szo KOZEPERE is talal ("ampa" ->
 * "parampam"). A szerver kiemeloje viszont szavakban gondolkodik, es egy ilyen
 * talalatot nem jelol meg. Ha nem jott jeloles, itt keressuk meg magunk -
 * kulonben a talalati listaban ott allna a sor, de nem latszana, MIERT.
 */
function Highlighted({ text, term }: { text: string; term: string }) {
  if (text.includes('<<')) {
    const parts = text.split(/(<<[^>]*>>)/g);
    return (
      <>
        {parts.map((part, index) =>
          part.startsWith('<<') && part.endsWith('>>') ? (
            <Mark key={index}>{part.slice(2, -2)}</Mark>
          ) : (
            <Fragment key={index}>{part}</Fragment>
          ),
        )}
      </>
    );
  }

  const needle = foldWithMap(term).folded.trim();
  if (!needle) return <>{text}</>;

  const { folded, map } = foldWithMap(text);
  const darabok: React.ReactNode[] = [];
  let olvasva = 0;
  let hol = folded.indexOf(needle);

  while (hol !== -1) {
    const kezdet = map[hol]!;
    const vege = map[hol + needle.length]!;
    if (kezdet > olvasva) darabok.push(text.slice(olvasva, kezdet));
    darabok.push(<Mark key={kezdet}>{text.slice(kezdet, vege)}</Mark>);
    olvasva = vege;
    hol = folded.indexOf(needle, hol + needle.length);
  }

  if (olvasva === 0) return <>{text}</>;
  darabok.push(text.slice(olvasva));

  return <>{darabok}</>;
}

function Mark({ children }: { children: React.ReactNode }) {
  return <mark className="rounded bg-alert-soft px-0.5 font-semibold text-alert">{children}</mark>;
}
